/**
 * Fractions and mixed numbers, for the line plots of school statistics ("measure each
 * pencil to the nearest 1/4 inch and make a line plot").
 *
 * Input like `1 1/2, 3/4, 2` reads as 1.5, 0.75 and 2, and the axis can be labelled
 * back in fractions. A mixed number is a whole number followed by a space and a proper
 * fraction, so in a list, separate values with commas, semicolons or new lines.
 */

import { parseCell } from "@/lib/tool-kit/charts/parse";

const FRACTION = /^([+\-−]?)(\d+)\/(\d+)$/;
const INTEGER = /^[+\-−]?\d+$/;
const WHITESPACE_RUN = /\s+/;
/** With fractions, spaces belong to mixed numbers: only these separate values. */
const LIST_SEPARATORS_WITH_SEMICOLON = /[\n;]+/;
const LIST_SEPARATORS = /[\n,;]+/;
/** Without fractions, the ordinary number-list separators. */
const TOKENS_WITH_SEMICOLON = /[\s;]+/;
const TOKENS = /[\s,;]+/;

export interface FractionList {
  values: number[];
  /** Denominators that appeared, for labelling the axis in the same units. */
  denominators: number[];
  invalid: string[];
}

function parseFraction(
  token: string
): { value: number; denominator: number } | null {
  const match = FRACTION.exec(token);
  if (!match) {
    return null;
  }
  const denominator = Number(match[3]);
  if (denominator === 0) {
    return null;
  }
  const sign = match[1] === "" || match[1] === "+" ? 1 : -1;
  return { value: (sign * Number(match[2])) / denominator, denominator };
}

/** Read one chunk of space-separated tokens, joining "1 1/2" into a mixed number. */
function readChunk(chunk: string, out: FractionList): void {
  const tokens = chunk.trim().split(WHITESPACE_RUN).filter(Boolean);
  let index = 0;
  while (index < tokens.length) {
    const token = tokens[index];
    const next = tokens[index + 1];
    const nextFraction = next === undefined ? null : parseFraction(next);
    if (INTEGER.test(token) && nextFraction && nextFraction.value > 0) {
      const whole = parseCell(token) ?? 0;
      const negative =
        whole < 0 || token.startsWith("-") || token.startsWith("−");
      const magnitude = Math.abs(whole) + nextFraction.value;
      out.values.push(negative ? -magnitude : magnitude);
      out.denominators.push(nextFraction.denominator);
      index += 2;
      continue;
    }
    const fraction = parseFraction(token);
    if (fraction) {
      out.values.push(fraction.value);
      out.denominators.push(fraction.denominator);
    } else {
      const value = parseCell(token);
      if (value === null) {
        out.invalid.push(token);
      } else {
        out.values.push(value);
      }
    }
    index += 1;
  }
}

/**
 * A list that may contain fractions. Without a slash anywhere it is an ordinary number
 * list (a semicolon frees the comma to be a decimal mark, as everywhere else).
 */
export function parseFractionList(text: string): FractionList {
  const out: FractionList = { values: [], denominators: [], invalid: [] };
  const semicolon = text.includes(";");
  let separators = semicolon ? TOKENS_WITH_SEMICOLON : TOKENS;
  if (text.includes("/")) {
    separators = semicolon ? LIST_SEPARATORS_WITH_SEMICOLON : LIST_SEPARATORS;
  }
  for (const chunk of text.split(separators)) {
    readChunk(chunk, out);
  }
  return out;
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x;
}

/** Least common multiple of the denominators (1 for none). */
export function commonDenominator(denominators: readonly number[]): number {
  let lcm = 1;
  for (const d of denominators) {
    lcm = (lcm / gcd(lcm, d)) * d;
  }
  return lcm;
}

/**
 * `value` as a whole number, proper fraction or mixed number over `denominator`,
 * reduced: 1.5 over 4 is "1 1/2", 0.75 over 4 is "3/4", 2 over 4 is "2". Returns null
 * when the value is not a whole number of 1/denominator steps.
 */
export function formatFraction(
  value: number,
  denominator: number,
  minus = "−"
): string | null {
  const numerator = Math.round(Math.abs(value) * denominator);
  if (Math.abs(Math.abs(value) * denominator - numerator) > 1e-9) {
    return null;
  }
  const sign = value < 0 && numerator !== 0 ? minus : "";
  const whole = Math.floor(numerator / denominator);
  const remainder = numerator % denominator;
  if (remainder === 0) {
    return `${sign}${whole}`;
  }
  const divisor = gcd(remainder, denominator);
  const fraction = `${remainder / divisor}/${denominator / divisor}`;
  return whole === 0 ? `${sign}${fraction}` : `${sign}${whole} ${fraction}`;
}
